import { useState, useCallback, useRef, useMemo } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { CalendarDays } from 'lucide-react';

import { MasterPageHeader }   from '@/components/master/MasterPageHeader';
import { MasterLookupBar, MasterCodeLookup } from '@/components/master/MasterLookupBar';
import { MasterFlash }         from '@/components/master/MasterFlash';
import { MasterAuditTab }      from '@/components/master/MasterAuditTab';
import { MasterConfirmDialogs } from '@/components/master/MasterConfirmDialogs';
import { MasterToolbar }       from '@/components/master/MasterToolbar';
import { MasterSearchModal }   from '@/components/master/MasterSearchModal';
import type { SearchColumn }   from '@/components/master/MasterSearchModal';
import { MasterReportPreview } from '@/components/master/report/MasterReportPreview';
import { ReportHeader }        from '@/components/master/report/ReportHeader';
import { ReportSection }       from '@/components/master/report/ReportSection';
import { ReportFooter }        from '@/components/master/report/ReportFooter';
import {
  Badge, Card, Field, Input, Tabs, Textarea, Toggle,
} from '@/components/ui';

import { useAuth }             from '@/hooks/useAuth';
import { usePermission }       from '@/hooks/usePermission';
import { useFlashMessage }     from '@/hooks/useFlashMessage';
import { useMasterScreenState } from '@/hooks/useMasterScreenState';
import { useMasterRecord, masterListFetcher } from '@/hooks/useMasterRecord';
import { Permissions }         from '@/constants/permissions';
import { financialYearApi }    from '@/api/financialYear.api';
import type { FinancialYearDto, FinancialYearListDto } from '@/types/financialYear.types';
import { cn } from '@/utils/cn';
import { formatDateTime } from '@/utils/format';

// ── Zod schema ─────────────────────────────────────────────────────────────────

const schema = z.object({
  yearCode:    z.string().min(1, 'Year code is required').max(20),
  description: z.string().min(1, 'Description is required').max(200),
  startDate:   z.string().min(1, 'Start date is required'),
  endDate:     z.string().min(1, 'End date is required'),
  isCurrent:   z.boolean(),
  remarks:     z.string().max(500).nullable().optional(),
});

type FormValues = z.infer<typeof schema>;

// ── Constants ─────────────────────────────────────────────────────────────────

const EMPTY_FORM: FormValues = {
  yearCode: '', description: '', startDate: '', endDate: '',
  isCurrent: false, remarks: null,
};

const TABS = ['General', 'Audit'] as const;
type Tab = (typeof TABS)[number];

const SEARCH_COLUMNS: SearchColumn<FinancialYearListDto>[] = [
  { key: 'yearCode',     label: 'Year Code',  className: 'w-24 font-mono' },
  { key: 'description',  label: 'Description' },
  { key: 'startDate',    label: 'Start Date', className: 'w-28' },
  { key: 'endDate',      label: 'End Date',   className: 'w-28' },
  { key: 'yearEndStatus', label: 'Status',    className: 'w-28',
    render: r => {
      const colour =
        r.yearEndStatus === 'OPEN'         ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300' :
        r.yearEndStatus === 'TEMP_CLOSED'  ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300' :
        r.yearEndStatus === 'FINAL_CLOSED' ? 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300' :
        'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400';
      return <span className={cn('px-1.5 py-0.5 rounded text-[10px] font-semibold', colour)}>{r.yearEndStatus}</span>;
    },
  },
  { key: 'isCurrent', label: 'Current', className: 'w-20',
    render: r => r.isCurrent ? <Badge variant="success">Current</Badge> : null },
];

// ── Helpers ────────────────────────────────────────────────────────────────────

function dtoToForm(dto: FinancialYearDto): FormValues {
  return {
    yearCode:    dto.yearCode,
    description: dto.description,
    startDate:   dto.startDate,
    endDate:     dto.endDate,
    isCurrent:   dto.isCurrent,
    remarks:     dto.remarks ?? null,
  };
}

function statusBadgeClass(status: string): string {
  if (status === 'OPEN')         return 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300';
  if (status === 'TEMP_CLOSED')  return 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300';
  if (status === 'FINAL_CLOSED') return 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300';
  return 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400';
}

// ── Component ─────────────────────────────────────────────────────────────────

export const FinancialYearPage = () => {
  const { user }          = useAuth();
  const { hasPermission } = usePermission();

  const canView   = hasPermission(Permissions.MST_FINANCIAL_YEAR_VIEW);
  const canCreate = hasPermission(Permissions.MST_FINANCIAL_YEAR_CREATE);
  const canEdit   = hasPermission(Permissions.MST_FINANCIAL_YEAR_EDIT);
  const canDelete = hasPermission(Permissions.MST_FINANCIAL_YEAR_DELETE);

  const screen = useMasterScreenState({ canEdit, canDelete });

  const { flashMsg, flash } = useFlashMessage();
  const codeInputRef = useRef<HTMLInputElement>(null);

  const [codeInput,    setCodeInput]    = useState('');

  const [showNewConfirm,    setShowNewConfirm]    = useState<string | null>(null);

  const {
    register, handleSubmit, control, reset, watch,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: EMPTY_FORM,
  });

  const isCurrent = watch('isCurrent');

  // ── Helpers ───────────────────────────────────────────────────────────────────
  const master = useMasterRecord<FinancialYearDto, Tab, FormValues>({
    screen, flash, firstTab: 'General', focusRef: codeInputRef,
    clearForm:   () => reset(EMPTY_FORM),
    clearScreen: () => { setCodeInput(''); },
    onDelete: {
      remove:   id => financialYearApi.remove(id),
      describe: r => `Financial year '${r.yearCode}'`,
    },
  });

  const {
    record, setRecord, activeTab, setActiveTab, saving, setSaving, lookupBusy, setLookupBusy,
    printOpen, setPrintOpen, searchOpen, setSearchOpen,
    pendingSave, setPendingSave, showSaveConfirm, setShowSaveConfirm,
    showDeleteConfirm, setShowDeleteConfirm, resetToInitial, executeDelete, beginSave,
  } = master;


  const loadRecord = useCallback((dto: FinancialYearDto) => {
    setRecord(dto);
    reset(dtoToForm(dto));
    setCodeInput(dto.yearCode);
    screen.setMode('view');
    setActiveTab('General');
  }, [reset, screen]);

  // ── Code lookup ───────────────────────────────────────────────────────────────
  const handleCodeLookup = async () => {
    const code = codeInput.trim().toUpperCase();
    if (!code) return;
    if (!canView) { flash('error', 'You do not have permission to view financial years.'); return; }
    setLookupBusy(true);
    try {
      const res = await financialYearApi.getByCode(code);
      if (res.data.data) loadRecord(res.data.data);
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } }).response?.status;
      if (status === 404) {
        if (!canCreate) { flash('error', `Financial year '${code}' was not found.`); return; }
        setShowNewConfirm(code);
      } else {
        flash('error', 'Lookup failed. Please try again.');
      }
    } finally {
      setLookupBusy(false);
    }
  };

  const handleConfirmCreateNew = () => {
    const code = showNewConfirm;
    setShowNewConfirm(null);
    if (!code) return;
    reset({ ...EMPTY_FORM, yearCode: code });
    setCodeInput(code);
    setRecord(null);
    screen.setMode('new');
    setActiveTab('General');
  };

  // ── Toolbar ───────────────────────────────────────────────────────────────────
  const handleSaveClick = () => {
    // The second argument runs when the form is invalid. Without it a failed validation did
    // nothing at all -- no save, no message, no focus -- so the button looked broken rather than
    // the form looking wrong.
    handleSubmit(beginSave, (formErrors) => {
      const first = Object.values(formErrors)[0]?.message;
      flash('error', first ? String(first) : 'Please correct the highlighted fields.');
    })();
  };

  const executeSave = async () => {
    if (!pendingSave) return;
    setShowSaveConfirm(false);
    setSaving(true);
    try {
      const d = pendingSave;
      if (screen.isNew) {
        await financialYearApi.create({
          yearCode:    d.yearCode.trim().toUpperCase(),
          description: d.description.trim(),
          startDate:   d.startDate,
          endDate:     d.endDate,
          remarks:     d.remarks?.trim() || null,
        });
        flash('success', 'Financial year created successfully.');
        resetToInitial();
      } else if (screen.isEdit && record) {
        await financialYearApi.update(record.id, {
          description: d.description.trim(),
          startDate:   d.startDate,
          endDate:     d.endDate,
          remarks:     d.remarks?.trim() || null,
          isCurrent:   d.isCurrent,
        });
        flash('success', 'Financial year updated successfully.');
        resetToInitial();
      }
    } catch (err: unknown) {
      const body = (err as { response?: { data?: { message?: string;
                       errors?: { message?: string }[] } } }).response?.data;
      // errors[0] names the field. The envelope's own message is just "Validation failed."
      const msg = body?.errors?.[0]?.message ?? body?.message ?? 'Save failed. Please try again.';
      flash('error', msg);
    } finally {
      setSaving(false);
      setPendingSave(null);
    }
  };


  // ── Search modal ──────────────────────────────────────────────────────────────
  const fetchYears = useMemo(() => masterListFetcher(financialYearApi.getList), []);

  const handleSearchSelect = (item: FinancialYearListDto) => {
    setSearchOpen(false);
    setLookupBusy(true);
    financialYearApi.getById(item.id)
      .then(res => { if (res.data.data) loadRecord(res.data.data); })
      .catch(() => flash('error', 'Failed to load financial year.'))
      .finally(() => setLookupBusy(false));
  };

  const fieldsDisabled = screen.isInitial || screen.isView;

  // ── Render ────────────────────────────────────────────────────────────────────
  return (
    <div className="flex flex-col h-full min-h-0">

      <MasterPageHeader icon={CalendarDays} title="Financial Year Maintenance"
        subtitle="Manage accounting years and year-end status" screen={screen} />

      {/* Toolbar */}
      <MasterToolbar
        {...screen.toolbar} {...master.toolbar}
        saving={saving} loading={lookupBusy} onSave={handleSaveClick}
      />

      {/* Flash message */}
      <MasterFlash message={flashMsg} />

      <MasterLookupBar
        label="Year Code"
        summary={record && (<>
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300 truncate max-w-xs">
              {record.description}
            </span>
            <span className={cn('px-1.5 py-0.5 rounded text-[10px] font-semibold', statusBadgeClass(record.yearEndStatus))}>
              {record.yearEndStatus}
            </span>
            {record.isCurrent && (
              <span className="px-1.5 py-0.5 rounded bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 text-[10px] font-bold">
                CURRENT
              </span>
            )}
        </>)}
        hint={screen.isInitial ? "Enter a year code and press Enter, or click the search icon to browse." : undefined}
      >
        <MasterCodeLookup
          inputRef={codeInputRef}
          value={codeInput}
          onChange={setCodeInput}
          onSubmit={handleCodeLookup}
          onBrowse={() => setSearchOpen(true)}
          browseLabel="Search financial years"
          placeholder="Type year code + Enter…"
          disabled={screen.isNew || screen.isEdit || saving}
          busy={lookupBusy}
        />
      </MasterLookupBar>

      {/* Main content */}
      <div className="flex-1 overflow-auto">
        <form onSubmit={e => e.preventDefault()} noValidate>

          {/* Tabs */}
          <Tabs tabs={TABS} active={activeTab} onChange={setActiveTab} />

          <div className="p-5 space-y-5 max-w-2xl">

            {/* General tab */}
            {activeTab === 'General' && (
              <Card>
                <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-4">
                  Year Details
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">

                  <Input
                    label="Year Code"
                    {...register('yearCode')}
                    disabled
                    placeholder="— enter via code field above —"
                    className="font-mono uppercase"
                    hint={screen.isNew ? 'Cannot be changed after creation.' : undefined}
                  />

                  <Input
                    label="Description *"
                    {...register('description')}
                    error={errors.description?.message}
                    disabled={fieldsDisabled}
                    placeholder="e.g. Financial Year 2026"
                  />

                  <Input label="Start Date *" type="date" {...register('startDate')}
                    disabled={fieldsDisabled} error={errors.startDate?.message} />

                  <Input label="End Date *" type="date" {...register('endDate')}
                    disabled={fieldsDisabled} error={errors.endDate?.message} />

                  {/* Year End Status — VIEW only badge */}
                  {(screen.isView || screen.isEdit) && record && (
                    <Field label="Year End Status" className="sm:col-span-2"
                      hint="System-managed — use Year-End Close process to change.">
                      <span className={cn('px-2.5 py-1 rounded-full text-xs font-semibold', statusBadgeClass(record.yearEndStatus))}>
                        {record.yearEndStatus}
                      </span>
                    </Field>
                  )}

                  {/* Is Current Year toggle — VIEW/EDIT only */}
                  {(screen.isEdit || screen.isView) && (
                    <Field label="Current Year" className="sm:col-span-2">
                      <Controller
                        name="isCurrent"
                        control={control}
                        render={({ field }) => (
                          <Toggle
                            label={isCurrent ? 'Yes — this is the current accounting year' : 'No'}
                            checked={field.value}
                            onChange={field.onChange}
                            disabled={fieldsDisabled}
                          />
                        )}
                      />
                    </Field>
                  )}

                  {/* Remarks */}
                  <Textarea label="Remarks" {...register('remarks')} disabled={fieldsDisabled}
                    rows={3} maxLength={500} placeholder="Optional notes…"
                    className="resize-none" fieldClassName="sm:col-span-2" />
                </div>
              </Card>
            )}

            {/* Audit tab */}
            {activeTab === 'Audit' && <MasterAuditTab record={record} />}
          </div>
        </form>
      </div>

      {/* Modals */}
      <MasterSearchModal<FinancialYearListDto>
        isOpen={searchOpen}
        onClose={() => setSearchOpen(false)}
        title="Browse Financial Years"
        columns={SEARCH_COLUMNS}
        fetchData={fetchYears}
        onSelect={handleSearchSelect}
      />

      <MasterConfirmDialogs
        entity="Financial year"
        screen={screen}
        record={record}
        describe={r => `${r.yearCode} — ${r.description}`}
        saving={saving}
        create={{
          label: showNewConfirm,
          onConfirm: handleConfirmCreateNew,
          onCancel: () => setShowNewConfirm(null),
        }}
        saveOpen={showSaveConfirm}
        onSaveConfirm={executeSave}
        onSaveCancel={() => { setShowSaveConfirm(false); setPendingSave(null); }}
        deleteOpen={showDeleteConfirm}
        onDeleteConfirm={executeDelete}
        onDeleteCancel={() => setShowDeleteConfirm(false)}
      />

      {record && (
        <MasterReportPreview
          isOpen={printOpen}
          onClose={() => setPrintOpen(false)}
          title="Financial Year Report"
        >
          <ReportHeader
            reportTitle="Financial Year Master Report"
            printedBy={user?.fullName ?? user?.username}
            printedAt={new Date()}
            meta={[
              { label: 'Year Code', value: record.yearCode },
              { label: 'Status',    value: record.yearEndStatus },
            ]}
          />
          <ReportSection
            title="Year Details"
            fields={[
              { label: 'Year Code',        value: record.yearCode },
              { label: 'Description',      value: record.description, wide: true },
              { label: 'Start Date',       value: record.startDate },
              { label: 'End Date',         value: record.endDate },
              { label: 'Year End Status',  value: record.yearEndStatus },
              { label: 'Is Current Year',  value: record.isCurrent, display: record.isCurrent ? 'Yes' : 'No' },
              { label: 'Remarks',          value: record.remarks, wide: true },
            ]}
          />
          <ReportSection
            title="Audit Information"
            fields={[
              { label: 'Created By',    value: record.createdByName ?? record.createdBy },
              { label: 'Created Date',  value: formatDateTime(record.createdDate) },
              { label: 'Modified By',   value: record.modifiedByName ?? record.modifiedBy },
              { label: 'Modified Date', value: formatDateTime(record.modifiedDate) },
            ]}
          />
          <ReportFooter />
        </MasterReportPreview>
      )}
    </div>
  );
};
